import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { raelaChat, type RaelaMessage } from "@/lib/aiClient";
import { moderateMessage } from "@/lib/safety/moderateMessage";
import type { LiveAppId as AppId } from "@/lib/apps";
import type { RaelaMessageRow, RaelaThreadRow } from "./types";

export const useRaelaThreads = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState<RaelaThreadRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setThreads([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("raela_threads")
      .select("*")
      .is("archived_at", null)
      .order("updated_at", { ascending: false });
    setThreads((data as RaelaThreadRow[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = useCallback(
    async (app: AppId | null) => {
      if (!user) return { id: null as string | null, error: "not_signed_in" };
      const { data, error } = await supabase
        .from("raela_threads")
        .insert({ user_id: user.id, app_context: app, title: null })
        .select("id")
        .single();
      if (error) return { id: null, error: error.message };
      await load();
      return { id: (data as { id: string }).id, error: null };
    },
    [user, load],
  );

  const archive = useCallback(
    async (id: string) => {
      const { error } = await supabase
        .from("raela_threads")
        .update({ archived_at: new Date().toISOString() })
        .eq("id", id);
      if (!error) setThreads((prev) => prev.filter((t) => t.id !== id));
      return { error: error?.message ?? null };
    },
    [],
  );

  return { threads, loading, reload: load, create, archive };
};

export const useRaelaThread = (threadId: string | null, app: AppId | null) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<RaelaMessageRow[]>([]);
  const [traceByMessageId, setTraceByMessageId] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!threadId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("raela_messages")
      .select("*")
      .eq("thread_id", threadId)
      .order("created_at", { ascending: true });
    setMessages((data as RaelaMessageRow[]) ?? []);
    setLoading(false);
  }, [threadId]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = useCallback(
    async (text: string) => {
      if (!user || !threadId || !text.trim()) return { error: "invalid" };
      setSending(true);

      // Optimistic insert of user message
      const userRow = {
        thread_id: threadId,
        user_id: user.id,
        role: "user" as const,
        content: text.trim(),
      };
      const { data: insertedUser, error: insErr } = await supabase
        .from("raela_messages")
        .insert(userRow)
        .select("*")
        .single();
      if (insErr) {
        setSending(false);
        return { error: insErr.message };
      }
      setMessages((prev) => [...prev, insertedUser as RaelaMessageRow]);

      // RAELA Function 1 — classify the user message (fire-and-forget).
      moderateMessage(
        "raela",
        (insertedUser as RaelaMessageRow).id,
        text.trim(),
      );

      // Build history for the model
      const history: RaelaMessage[] = [
        ...messages.map((m) => ({ role: m.role, content: m.content })),
        { role: "user", content: text.trim() },
      ];

      try {
        const out = await raelaChat({
          messages: history,
          app: (app ?? "fryqnc") as AppId,
          surface: "chat",
          memberId: user.id,
        });

        // Persist assistant reply
        const { data: insertedAsst } = await supabase
          .from("raela_messages")
          .insert({
            thread_id: threadId,
            user_id: user.id,
            role: "assistant",
            content: out.text,
            flagged: out.crisis,
            flag_severity: out.crisis ? "crisis" : null,
          })
          .select("*")
          .single();
        if (insertedAsst) {
          const row = insertedAsst as RaelaMessageRow;
          setMessages((prev) => [...prev, row]);
          if (out.traceId) {
            setTraceByMessageId((m) => ({ ...m, [row.id]: out.traceId }));
          }
        }
        // Bump thread updated_at
        await supabase
          .from("raela_threads")
          .update({ updated_at: new Date().toISOString() })
          .eq("id", threadId);
        setSending(false);
        return { error: null };
      } catch (e) {
        setSending(false);
        return {
          error: e instanceof Error ? e.message : "RAELA is having trouble",
        };
      }
    },
    [user, threadId, messages, app],
  );

  return { messages, traceByMessageId, loading, sending, send, reload: load };
};
